import useGlobalUIStore from '@/stores';
import {logger} from '@/utils/logger';
import {
  ErrorPageProps,
  SoopButtonCommon,
  SoopErrorPage,
} from '@soop-ui/soop-ui';
import React, {useEffect} from 'react';
import {useRouteError} from 'react-router-dom';

/**
 * 라우트 에러 타입 정의
 */
interface RouteError {
  status?: number;
  statusText?: string;
  message?: string;
  stack?: string;
  name?: string;
}

/**
 * 에러 메시지 상수
 */
const ERROR_MESSAGES = {
  NOT_FOUND: {
    title: '다시 한 번 주소를 확인해 주세요.',
    description:
      '입력하신 주소의 페이지가 사라졌거나 다른 주소로 변경됐습니다.\nURL을 다시 한 번 확인해 주세요.',
    imgType: '404' as const,
  },
  DEFAULT: {
    title: '일시적으로 서비스에 접속할 수 없습니다.',
    description:
      '이용에 불편을 드려 죄송합니다.\n빠르게 해결하기 위해 노력하고 있으니 조금만 기다려 주세요.',
    imgType: 'disconnected' as const,
  },
} as const;

/**
 * 에러 페이지 컴포넌트
 * 라우트 에러가 발생했을 때 표시되는 폴백 UI
 */
const ErrorFallback = () => {
  const error = useRouteError();

  useEffect(() => {
    logError(error);
  }, [error]);

  const errorInfo = getErrorInfo(error);

  const handleGoToMain = (): void => {
    window.location.href = '/';
  };

  const {isDarkMode} = useGlobalUIStore();

  return (
    <SoopErrorPage
      title={errorInfo.title}
      description={renderMultilineText(errorInfo.description || '')}
      imgType={errorInfo.imgType}
      isDarkMode={isDarkMode}
      buttonElement={
        <SoopButtonCommon
          onClick={handleGoToMain}
          size={48}
          variant='defaultSecondary'>
          메인으로
        </SoopButtonCommon>
      }
    />
  );
};

/**
 * 에러 객체가 RouteError 타입인지 확인하는 타입 가드
 */
function isRouteError(error: unknown): error is RouteError {
  return (
    typeof error === 'object' &&
    error !== null &&
    ('status' in error || 'message' in error)
  );
}

/**
 * 줄바꿈이 포함된 텍스트를 JSX 요소로 변환
 */
function renderMultilineText(
  text: string | number | React.ReactNode,
): React.ReactElement {
  const textString = String(text);
  const lines = textString.split('\n');

  return (
    <>
      {lines.map((line, index) => (
        <span key={index}>
          {line}
          {index < lines.length - 1 && <br />}
        </span>
      ))}
    </>
  );
}

/**
 * 에러 상태에 따른 UI 정보 반환
 */
function getErrorInfo(error: unknown): ErrorPageProps {
  if (isRouteError(error) && error.status === 404) {
    return ERROR_MESSAGES.NOT_FOUND;
  }

  return ERROR_MESSAGES.DEFAULT;
}

/**
 * 클라이언트 컴포넌트 에러를 로깅하는 함수
 */
function logClientComponentError(error: Error): void {
  logger.fatal(error, {
    errorType: 'Client Component Error',
    errorCategory: 'React Component Error',
    errorSource: 'ErrorFallback',
    routePath: window.location.pathname,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString(),
    // 클라이언트 에러 특화 정보
    errorName: error.name,
    errorMessage: error.message,
    errorStack: error.stack,
    isRouteError: false,
    errorSeverity: 'high',
  });
}

/**
 * React Router 라우트 에러를 로깅하는 함수
 */
function logRouteError(error: RouteError): void {
  const routeError = new Error(
    `Route Error ${error.status}: ${error.statusText || 'Unknown status'}`,
  );

  // HTTP 상태 코드별로 에러 타입 세분화
  const status = error.status || 0;
  const detailedErrorType = getDetailedErrorType(status);

  logger.fatal(routeError, {
    errorType: detailedErrorType,
    errorCategory: 'React Router Error',
    errorSource: 'ErrorFallback',
    routePath: window.location.pathname,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString(),
    // 원본 RouteError 정보 보존
    originalRouteError: error,
    status: error.status,
    statusText: error.statusText,
    data: 'data' in error ? (error as any).data : undefined,
    // 라우트 에러 특화 정보
    isRouteError: true,
    errorSeverity: status >= 500 ? 'high' : 'medium',
  });
}

/**
 * 알 수 없는 타입의 에러를 로깅하는 함수
 */
function logUnknownError(error: unknown): void {
  const unknownError = new Error(`Unknown Error: ${String(error)}`);

  logger.fatal(unknownError, {
    errorType: 'Unknown Error Type',
    errorCategory: 'Unknown Error',
    errorSource: 'ErrorFallback',
    routePath: window.location.pathname,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString(),
    // 원본 에러 정보
    originalError: error,
    originalErrorType: typeof error,
    originalErrorConstructor:
      error && typeof error === 'object'
        ? (error as any).constructor?.name
        : 'N/A',
    isRouteError: false,
    errorSeverity: 'unknown',
  });
}

/**
 * HTTP 상태 코드에 따른 상세 에러 타입을 반환하는 함수
 */
function getDetailedErrorType(status: number): string {
  if (status === 404) {
    return 'Route Not Found (404)';
  } else if (status >= 500) {
    return 'Server Error (5xx)';
  } else if (status >= 400) {
    return 'Client Error (4xx)';
  }
  return 'Route Error';
}

/**
 * 에러를 분류하고 적절한 로깅 함수를 호출하는 메인 함수
 */
function logError(error: unknown): void {
  if (error instanceof Error) {
    // 컴포넌트에서 발생한 클라이언트 에러 (우선순위 높음)
    logClientComponentError(error);
  } else if (isRouteError(error)) {
    // React Router 라우트 에러 (404, 500 등)
    logRouteError(error);
  } else {
    // 알 수 없는 타입의 에러
    logUnknownError(error);
  }
}

export default ErrorFallback;
